"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { hasAdminRole } from "@/lib/admin/permissions";
import { useSearchAPI } from "./useSearchAPI";
import {
  detectSearchType,
  isValidEmail,
  isValidLinkedIn,
  validateSearchInput,
  isSearchValid,
  isValidName,
} from "../utils/searchValidators";
import { getSmartSuggestion } from "../utils/searchSuggestions";
import { findSpecialProfileRedirect } from "../constants/specialProfileBypass";
import {
  hasUsableCandidateDetails,
  toRawCandidateFromSearchResult,
} from "@/lib/search/rawCandidates";
import { mergeCompanyMetadata, readCompanyMetadata } from "@/lib/search/companyMatching";

const EXTERNAL_LOAD_MORE_LIMIT = 9;

const candidateVanity = (candidate) =>
  candidate?.linkedinUrl
    ?.match(/linkedin\.com\/in\/([^/?#]+)/i)?.[1]
    ?.toLowerCase() || "";

const candidateNameCompany = (candidate) =>
  [candidate?.firstName, candidate?.lastName, candidate?.company]
    .map((part) =>
      String(part || "")
        .trim()
        .toLowerCase(),
    )
    .join("|");

// RC repeats profiles across cursor pages, and a DB candidate already in the
// table can come back from RC under its real job title. Two people with the
// same name at the same company are only treated as distinct when both carry a
// LinkedIn slug to tell them apart.
function dedupeAgainstExisting(existing, incoming) {
  const seenVanities = new Set();
  const seenNameCompany = new Set();
  for (const candidate of existing) {
    const vanity = candidateVanity(candidate);
    if (vanity) seenVanities.add(vanity);
    else seenNameCompany.add(candidateNameCompany(candidate));
  }

  return incoming.filter((candidate) => {
    const vanity = candidateVanity(candidate);
    const nameCompany = candidateNameCompany(candidate);
    if (vanity && seenVanities.has(vanity)) return false;
    if (seenNameCompany.has(nameCompany)) return false;
    if (vanity) seenVanities.add(vanity);
    else seenNameCompany.add(nameCompany);
    return true;
  });
}

export const useSearch = () => {
  const router = useRouter();
  const { data: session } = useSession();
  const unlimitedSearch = hasAdminRole(session);
  const externalLoadMoreLimit = unlimitedSearch ? Infinity : EXTERNAL_LOAD_MORE_LIMIT;
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("name");
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [preventSync, setPreventSync] = useState(false);
  const [isNameOnlySearch, setIsNameOnlySearch] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [skeletonMode, setSkeletonMode] = useState("profile");
  const [tableSkeletonReady, setTableSkeletonReady] = useState(false);
  const [moreResults, setMoreResults] = useState([]);
  const [rawCandidates, setRawCandidates] = useState([]);
  const [rcPage, setRcPage] = useState(1);
  const [rcCursor, setRcCursor] = useState(null);
  const [rcSearchFilters, setRcSearchFilters] = useState(null);
  const [rcTotalCount, setRcTotalCount] = useState(null);
  const [hasLoadedRCCandidates, setHasLoadedRCCandidates] = useState(false);
  const [isLoadingMoreRC, setIsLoadingMoreRC] = useState(false);
  const [enrichingId, setEnrichingId] = useState(null);
  const [enrichedUrls, setEnrichedUrls] = useState(new Set());
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionLabel, setSuggestionLabel] = useState("");
  const [showTableNarrowHint, setShowTableNarrowHint] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [retryInitialSearch, setRetryInitialSearch] = useState(false);
  const [externalSearchUnavailable, setExternalSearchUnavailable] = useState(
    false,
  );
  const [searchCooldown, setSearchCooldown] = useState(null);

  const [nameQuery, setNameQuery] = useState("");
  const [companyQuery, setCompanyQuery] = useState("");

  const inputRef = useRef(null);
  const focusSearchInputRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const searchGenerationRef = useRef(0);
  const countRequestedGenerationRef = useRef(-1);
  const [savedCursor, setSavedCursor] = useState(null);
  const externalMoreCountRef = useRef(0);
  const [externalLimitReached, setExternalLimitReached] = useState(false);
  const renderGeneration = searchGenerationRef.current;

  useEffect(() => {
    if (!focusSearchInputRef.current || showResults || showSkeleton) return;
    const input = inputRef.current;
    if (!input) return;
    focusSearchInputRef.current = false;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, [showResults, showSkeleton]);

  const {
    performSearch,
    performDiscoverSearch,
    fetchPeopleCount,
    loadMoreRCCandidates,
    loadMoreSavedProfiles,
    performEnrichOne,
  } = useSearchAPI();

  useEffect(() => {
    const generation = renderGeneration;
    if (generation !== searchGenerationRef.current || !showResults || searchType !== "discover" || !rcSearchFilters || rcTotalCount != null ||
        countRequestedGenerationRef.current === generation) return;
    countRequestedGenerationRef.current = generation;
    // Add the optional count without delaying, clearing, or changing result rows.
    void fetchPeopleCount(searchQuery, rcSearchFilters).then((count) => {
      if (generation === searchGenerationRef.current && typeof count === "number" && count >= 0) {
        setRcTotalCount((current) => current ?? count);
      }
    });
  }, [showResults, searchType, searchQuery, rcSearchFilters, rcTotalCount, renderGeneration]);

  const openProfile = (result, fresh = false) => {
    const profileId = result.url || result.id;
    if (!profileId) return false;
    const path = `/profile/${encodeURIComponent(profileId)}`;
    // A fresh URL bypasses a cached "Not Found" without reloading the document.
    const destination = fresh || result.externalEnriched
      ? `${path}?updated=${Date.now()}`
      : path;
    router.push(destination);
    return true;
  };

  const updateRCPagination = (searchResponse = {}) => {
    setSavedCursor(searchResponse.savedCursor || null);
    const filters = searchResponse.rcSearchFilters || null;
    setRcCursor(searchResponse.rcNextCursor || null);
    setRcSearchFilters(filters);
    if (searchResponse.rcTotalCount != null) {
      setRcTotalCount(searchResponse.rcTotalCount);
    }
    setHasLoadedRCCandidates(
      Boolean(filters) && searchResponse.rcSearchDeferred !== true &&
        searchResponse.externalSearchUnavailable !== true,
    );
  };

  const resetSearch = () => {
    externalMoreCountRef.current = 0;
    setExternalLimitReached(false);
    searchGenerationRef.current++;
    loadingMoreRef.current = false;
    setRetryInitialSearch(false);
    setSavedCursor(null);
    focusSearchInputRef.current = true;
    setSearchQuery("");
    setNameQuery("");
    setCompanyQuery("");
    setSearchError("");
    setExternalSearchUnavailable(false);
    setSearchCooldown(null);
    setResults([]);
    setShowResults(false);
    setIsLoading(false);
    setPreventSync(false);
    setIsNameOnlySearch(false);
    setMoreResults([]);
    setRawCandidates([]);
    setRcPage(1);
    setRcCursor(null);
    setRcSearchFilters(null);
    setRcTotalCount(null);
    setHasLoadedRCCandidates(false);
    setEnrichingId(null);
    setEnrichedUrls(new Set());
    setSuggestions([]);
    setSuggestionLabel("");
    setShowTableNarrowHint(false);
    setShowSkeleton(false);
    setSkeletonMode("profile");
    setTableSkeletonReady(false);
    setIsLoadingMoreRC(false);

    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.focus();
    }
  };

  const refineSearch = () => {
    const currentQuery = searchQuery;
    resetSearch();
    setSearchQuery(currentQuery);
    setSearchType(detectSearchType(currentQuery));
  };

  const handleSearch = async (queryOverride = null, searchOptions = {}) => {
    const effectiveQuery = typeof queryOverride === "string" ? queryOverride : searchQuery;
    if (!effectiveQuery.trim()) return;
    const generation = ++searchGenerationRef.current;
    externalMoreCountRef.current = 0;
    setExternalLimitReached(false);
    const isCurrentSearch = () => generation === searchGenerationRef.current;
    const currentType = detectSearchType(effectiveQuery);
    loadingMoreRef.current = false;
    setSearchQuery(effectiveQuery);
    setSearchType(currentType);
    setSavedCursor(null);
    setNameQuery("");
    setCompanyQuery("");
    setSearchError("");
    setRetryInitialSearch(false);
    setExternalSearchUnavailable(false);
    setSearchCooldown(null);
    setShowResults(false);
    setShowSkeleton(false);
    setResults([]);
    setMoreResults([]);
    setRawCandidates([]);
    setSuggestions([]);
    setSuggestionLabel("");
    setShowTableNarrowHint(false);
    setRcCursor(null);
    setRcSearchFilters(null);
    setRcTotalCount(null);
    setHasLoadedRCCandidates(false);
    setIsLoadingMoreRC(false);
    setEnrichingId(null);
    setEnrichedUrls(new Set());
    setRcPage(1);
    setIsNameOnlySearch(false);

    const directProfileId = findSpecialProfileRedirect(effectiveQuery);
    if (directProfileId) {
      setIsLoading(false);
      router.push(`/profile/${directProfileId}`);
      return;
    }
    if (currentType !== "discover" && !validateSearchInput(effectiveQuery, currentType)) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setSkeletonMode(currentType === "discover" ? "table" : "profile");
    setTableSkeletonReady(false);
    setShowSkeleton(currentType !== "discover");

    let navigatingToProfile = false;
    try {
      if (currentType === "discover") {
        const discoverQuery = effectiveQuery.trim();
        const isPersonLookupQuery = isValidName(discoverQuery) || /\s+at\s+/i.test(discoverQuery);
        const onRcCount = (count) => {
          if (!isCurrentSearch() || typeof count !== "number") return;
          setRcTotalCount(count);
          if (count > 0) setShowSkeleton(true);
        };
        const response = await performDiscoverSearch(discoverQuery, {
          broadenSearch: searchOptions.broadenSearch || null,
          deferExternalUntilUserClick: isPersonLookupQuery,
          streamRcCount: isPersonLookupQuery,
          onRcCount,
        });
        if (!isCurrentSearch()) return;
        if (response.revisionRequired) {
          setSearchError(response.revisionRequired);
          return;
        }
        if (response.flagged) {
          setSearchError("Please describe a role, person, or company to find professionals.");
          return;
        }
        updateRCPagination(response);
        setRetryInitialSearch(response.retryInitialSearch === true);
        setExternalSearchUnavailable(response.externalSearchUnavailable === true || response.savedSearchUnavailable === true);
        setSearchCooldown(response.externalSearchCooldown || null);
        const isNameSearch = response.queryType === "name";
        setIsNameOnlySearch(isNameSearch);
        setNameQuery(isNameSearch ? response.parsedName || discoverQuery : "");
        setCompanyQuery(response.parsedCompany || "");
        let profiles = response.transformedResults || [];
        let candidates = response.rawCandidates || [];
        setResults(profiles);
        setMoreResults(response.moreResults || []);
        setShowTableNarrowHint(response.showNarrowSearchHint === true);
        setSuggestions(response.suggestions || []);
        setSuggestionLabel(response.suggestionLabel || "");

        // Complete the initial discovery batch together. Name matches stay in
        // the same list; selection opens the saved profile or its enrichment flow.
        if (!isNameSearch && !candidates.length && !response.savedCursor &&
            !response.retryInitialSearch && response.deferExternalSearch === true) {
          setShowSkeleton(true);
          const page = await loadMoreRCCandidates(discoverQuery, null, response.rcSearchFilters || null, { onRcCount });
          if (!isCurrentSearch()) return;
          if (page.revisionRequired) {
            setSearchError(page.revisionRequired);
            return;
          }
          if (page.externalSearchUnavailable) {
            setExternalSearchUnavailable(true);
            setSearchCooldown(page.externalSearchCooldown || null);
          } else {
            setSearchCooldown(null);
            setHasLoadedRCCandidates(true);
            setRcCursor(page.nextCursor || null);
            setRcSearchFilters(page.searchFilters || response.rcSearchFilters || null);
            if (page.totalCount != null) setRcTotalCount(page.totalCount);
            candidates = page;
          }
        }
        candidates = dedupeAgainstExisting(profiles.map(toRawCandidateFromSearchResult), candidates);
        // A lone candidate needs real saved details before it can be a profile card.
        if (!profiles.length && !(response.moreResults || []).length &&
            candidates.length === 1 && candidates[0].linkedinUrl &&
            !response.savedCursor && !response.externalSearchUnavailable && !response.savedSearchUnavailable) {
          setShowSkeleton(true);
          const card = await performEnrichOne(candidates[0]);
          if (!isCurrentSearch()) return;
          if (card?.externalSearchCooldown) {
            setSearchCooldown(card.externalSearchCooldown);
          }
          if (card && !card.externalSearchCooldown) {
            const confirmedCard = mergeCompanyMetadata(card, readCompanyMetadata(candidates[0]));
            profiles = [confirmedCard];
            candidates = [];
            setResults(profiles);
          }
        }
        setRawCandidates(candidates);
        setShowResults(true);
        return;
      }

      // Email and LinkedIn keep their direct profile lookup/opening behavior.
      const response = await performSearch(currentType, effectiveQuery.trim(), false, null);
      if (!isCurrentSearch()) return;
      setSearchCooldown(response.externalSearchCooldown || null);
      if (response.enrichmentFailed || response.externalSearchUnavailable || response.retryInitialSearch) {
        setRetryInitialSearch(true);
        setExternalSearchUnavailable(true);
        setShowResults(true);
        return;
      }
      const profiles = response.transformedResults || [];
      if (profiles.length === 1) {
        navigatingToProfile = openProfile(profiles[0], currentType === "linkedin");
        if (navigatingToProfile) return;
      }
      setResults(profiles);
      setIsNameOnlySearch(currentType === "name");
      setShowResults(true);
    } catch (error) {
      if (!isCurrentSearch()) return;
      console.error("Search error:", error);
      setRetryInitialSearch(true);
      setExternalSearchUnavailable(true);
      setShowResults(true);
    } finally {
      // Keep the loading view until navigation replaces the search page.
      if (isCurrentSearch() && !navigatingToProfile) {
        setIsLoading(false);
        setShowSkeleton(false);
      }
    }
  };

  const isSearchValidFunc = () => {
    return isSearchValid(searchQuery, detectSearchType);
  };

  const savedKeys = (profiles) => profiles.flatMap((profile) => {
    const id = profile.id || profile.profileId;
    const slug = candidateVanity(profile);
    return [id && `id:${id}`, slug && `li:${slug}`].filter(Boolean);
  });

  const fetchSavedProfiles = async (cursor, target, generation) => {
    const profiles = [];
    let unavailable = false;
    const existing = [...results, ...moreResults, ...rawCandidates];
    for (let page = 0; cursor && profiles.length < target && page < 3; page++) {
      const loaded = await loadMoreSavedProfiles(searchQuery, cursor, savedKeys([...existing, ...profiles]));
      if (generation !== searchGenerationRef.current) return { stale: true, profiles: [] };
      if (loaded.restartRequired) {
        refineSearch();
        setSearchError(loaded.revisionRequired || "This search expired. Please search again.");
        return { stale: true, profiles: [] };
      }
      const seen = new Set(savedKeys([...existing, ...profiles]));
      for (const profile of loaded.profiles) {
        const keys = savedKeys([profile]);
        if (keys.some((key) => seen.has(key))) continue;
        keys.forEach((key) => seen.add(key));
        profiles.push(profile);
      }
      const nextCursor = loaded.savedCursor;
      unavailable = loaded.unavailable === true;
      if (unavailable || nextCursor === cursor) { cursor = nextCursor || cursor; break; }
      cursor = nextCursor;
    }
    return { profiles, savedCursor: cursor, unavailable };
  };

  const handleShowMore = async () => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setIsLoadingMoreRC(true);
    const generation = searchGenerationRef.current;
    try {
      let buffered = moreResults;
      if (buffered.length < 5 && savedCursor) {
        const page = await fetchSavedProfiles(savedCursor, 5 - buffered.length, generation);
        if (page.stale) return;
        setSavedCursor(page.savedCursor);
        setExternalSearchUnavailable(page.unavailable);
        buffered = [...buffered, ...page.profiles];
      }
      setResults((prev) => [...prev, ...buffered.slice(0, 5)]);
      setMoreResults(buffered.slice(5));
    } finally {
      if (generation === searchGenerationRef.current) {
        loadingMoreRef.current = false;
        setIsLoadingMoreRC(false);
      }
    }
  };

  const handleLoadNextRCPage = async ({ minCandidates = null } = {}) => {
    if (isLoadingMoreRC || loadingMoreRef.current) return;
    if (!savedCursor && externalMoreCountRef.current >= externalLoadMoreLimit) return [];
    if (!savedCursor && !rcCursor && rcTotalCount === 0) return [];
    if (!savedCursor && hasLoadedRCCandidates && !rcCursor) return [];
    loadingMoreRef.current = true;
    setIsLoadingMoreRC(true);
    const generation = searchGenerationRef.current;
    try {
      const nextPage = rawCandidates.length > 0 ? rcPage + 1 : 1;
      // Name-list controls default to five; discovery can request a smaller gap.
      const target = Math.min(5, Math.max(1, minCandidates ?? 5));
      const savedCandidates = [];
      if (savedCursor) {
        const page = await fetchSavedProfiles(savedCursor, target, generation);
        if (page.stale) { const stale = []; stale.stale = true; return stale; }
        setSavedCursor(page.savedCursor);
        setExternalSearchUnavailable(page.unavailable);
        if (page.profiles.length) {
          setMoreResults((prev) => [...prev, ...page.profiles]);
          savedCandidates.push(...page.profiles.map((profile) => ({
            ...toRawCandidateFromSearchResult(profile), profileId: profile.url || profile.id || profile.sub,
          })));
        }
        savedCandidates.savedCursor = page.savedCursor;
        savedCandidates.nextCursor = page.savedCursor || rcCursor;
        savedCandidates.externalSearchUnavailable = page.unavailable;
        if (page.savedCursor || page.unavailable || savedCandidates.length >= target || (!rcCursor && rcTotalCount === 0) ||
            (hasLoadedRCCandidates && !rcCursor)) return savedCandidates;
      }
      if (externalMoreCountRef.current >= externalLoadMoreLimit) {
        return savedCandidates;
      }
      // Name and discovery clicks both skip duplicate/filtered batches,
      // retaining the cursor when the per-click request budget is reached.
      const maxPages = 3;
      const freshCandidates = [];
      const existing = [
        ...results.map(toRawCandidateFromSearchResult),
        ...moreResults.map(toRawCandidateFromSearchResult),
        ...rawCandidates,
        ...savedCandidates,
      ];
      let cursor = rcCursor;
      let filters = rcSearchFilters;
      for (let page = 0; page < maxPages; page++) {
        const remaining = externalLoadMoreLimit - externalMoreCountRef.current - freshCandidates.length;
        if (remaining <= 0) break;
        const newCandidates = await loadMoreRCCandidates(
          nameQuery || searchQuery,
          cursor,
          filters,
          { maxCandidates: remaining },
        );
        if (generation !== searchGenerationRef.current) { const stale = []; stale.stale = true; return stale; }
        if (newCandidates.revisionRequired) {
          refineSearch();
          setSearchError(newCandidates.revisionRequired);
          const stale = [];
          stale.stale = true;
          return stale;
        }
        if (newCandidates.externalSearchUnavailable) {
          freshCandidates.externalSearchUnavailable = true;
          freshCandidates.externalSearchCooldown =
            newCandidates.externalSearchCooldown || null;
          break;
        }
        setSearchCooldown(null);
        const nextCursor = newCandidates.nextCursor || null;
        filters = newCandidates.searchFilters || filters;
        setHasLoadedRCCandidates(true);
        setRcCursor(nextCursor);
        setRcSearchFilters(filters);
        if (newCandidates.totalCount != null) {
          setRcTotalCount(newCandidates.totalCount);
        }
        freshCandidates.push(
          ...dedupeAgainstExisting(
            [...existing, ...freshCandidates],
            newCandidates.filter(hasUsableCandidateDetails),
          ).slice(0, remaining),
        );
        freshCandidates.nextCursor = nextCursor;
        if (freshCandidates.length + externalMoreCountRef.current >= externalLoadMoreLimit || freshCandidates.length + savedCandidates.length >= target || !nextCursor || nextCursor === cursor) {
          break;
        }
        cursor = nextCursor;
      }
      // Publish all rows together after the requested group is ready.
      if (freshCandidates.length > 0) {
        externalMoreCountRef.current += freshCandidates.length;
        if (externalMoreCountRef.current >= externalLoadMoreLimit) {
          setExternalLimitReached(true);
          setRcCursor(null);
          freshCandidates.nextCursor = null;
          freshCandidates.externalSearchUnavailable = false;
        }
        setRawCandidates((prev) => [
          ...prev,
          ...dedupeAgainstExisting(prev, freshCandidates),
        ]);
        setRcPage(nextPage);
      }
      const combined = [...savedCandidates, ...freshCandidates];
      combined.nextCursor = freshCandidates.nextCursor;
      combined.externalSearchUnavailable = freshCandidates.externalSearchUnavailable;
      combined.externalSearchCooldown =
        freshCandidates.externalSearchCooldown || null;
      if (combined.externalSearchCooldown) {
        setSearchCooldown(combined.externalSearchCooldown);
      }
      return combined;
    } finally {
      if (generation === searchGenerationRef.current) {
        loadingMoreRef.current = false;
        setIsLoadingMoreRC(false);
      }
    }
  };

  const handleEnrichOne = (candidate, { newTab = false } = {}) => {
    const params = new URLSearchParams({
      linkedin: candidate.linkedinUrl || "",
      first: candidate.firstName || "",
      last: candidate.lastName || "",
      company: candidate.company || "",
    });
    const enrichUrl = `/search/enrich?${params.toString()}`;

    if (newTab) {
      window.open(enrichUrl, "_blank", "noopener,noreferrer");
      return;
    }

    setEnrichingId(candidate.linkedinUrl || "candidate");
    setSkeletonMode("profile");
    setShowSkeleton(true);
    setEnrichedUrls((prev) => new Set([...prev, candidate.linkedinUrl]));
    router.push(enrichUrl);
  };

  const loadedPeople = new Set([
    ...[...results, ...moreResults].map((profile) => ({
      ...toRawCandidateFromSearchResult(profile), profileId: profile.url || profile.id || profile.sub,
    })),
    ...rawCandidates,
  ].filter(hasUsableCandidateDetails).map((candidate) =>
    candidateVanity(candidate) || candidate.profileId || candidate.id || candidateNameCompany(candidate),
  ));
  // The count can avoid a redundant first lookup for a known single match.
  // Actual provider/saved cursors remain authoritative and are never hidden.
  const knownSingleMatch = loadedPeople.size === 1 && rcTotalCount === 1;

  return {
    searchQuery,
    setSearchQuery,
    searchType,
    setSearchType,
    isLoading,
    showSkeleton,
    skeletonMode,
    tableSkeletonReady,
    results,
    moreResults,
    rawCandidates,
    isLoadingMoreRC,
    enrichingId,
    enrichedUrls,
    showResults,
    suggestions,
    suggestionLabel,
    showTableNarrowHint,
    rcTotalCount,
    savedHasMore: Boolean(savedCursor),
    externalLimitReached: !unlimitedSearch && externalLimitReached,
    rcHasMore: (unlimitedSearch || !externalLimitReached) && (Boolean(rcCursor) || (Boolean(rcSearchFilters) && rcTotalCount !== 0 &&
      !hasLoadedRCCandidates && !knownSingleMatch)),
    retryInitialSearch,
    preventSync,
    setPreventSync,
    isNameOnlySearch,
    nameQuery,
    companyQuery,
    inputRef,
    detectSearchType,
    getSmartSuggestion,
    handleSearch,
    resetSearch,
    refineSearch,
    handleShowMore,
    handleLoadNextRCPage,
    handleEnrichOne,
    isSearchValid: isSearchValidFunc,
    isValidEmail,
    isValidLinkedIn,
    searchError,
    externalSearchUnavailable,
    searchCooldown: unlimitedSearch ? null : searchCooldown,
    setSearchError,
  };
};
